import { useState } from 'react';

/**
 * Typing the answer instead of picking it.
 *
 * Four options means a quarter of every question is available by touching the
 * screen, and a capable child answers in two seconds without doing the maths.
 * Once they can pick a skill reliably the options stop being sent and this
 * appears instead - the same pad the column-addition screen has always used,
 * pulled out so every numeric activity can have it.
 *
 * Nothing submits until they press the button. A child changing their mind
 * mid-number must never have a half-typed answer graded.
 */
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
const MAX_DIGITS = 4;

export function NumberPad({
  disabled,
  onSubmit,
}: {
  disabled?: boolean;
  onSubmit: (answer: string) => void;
}) {
  const [typed, setTyped] = useState('');

  const press = (key: string) => {
    if (disabled) return;
    setTyped((current) => (current.length >= MAX_DIGITS ? current : current + key));
  };

  const rub = () => {
    if (disabled) return;
    setTyped((current) => current.slice(0, -1));
  };

  // A leading zero is a child counting, not a different number: "07" is 7.
  const answer = typed.replace(/^0+(?=\d)/, '');

  return (
    <div className="number-pad">
      <output className="number-pad__shown" aria-live="polite" aria-label="Your answer">
        {typed === '' ? '?' : typed}
      </output>

      <div className="keypad">
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className="keypad__key"
            disabled={disabled}
            onClick={() => press(key)}
          >
            {key}
          </button>
        ))}
        <button
          type="button"
          className="keypad__key keypad__key--rub"
          onClick={rub}
          disabled={disabled || typed === ''}
        >
          Rub out
        </button>
      </div>

      <button
        type="button"
        className="btn btn--big"
        disabled={disabled || typed === ''}
        onClick={() => onSubmit(answer)}
      >
        That&rsquo;s my answer
      </button>
    </div>
  );
}
